import type { DailyScore, MatchOutcome } from "@obean/game-contract";
import { dailyTier } from "@obean/game-contract";
import { useEffect, useState } from "preact/hooks";
import type { DailyEveryone } from "../net/api.js";
import { api } from "../net/api.js";

/** Below this many others, a share would say more about chance than about the day. */
export const SHARE_FROM = 5;

/** Where a play falls among today's others, in words (ADR 0041). */
export function everyoneText(e: Pick<DailyEveryone, "played" | "below">): string {
  const others = Math.max(0, e.played - 1);
  if (others === 0) return "You're the first to finish today's daily.";
  if (others < SHARE_FROM)
    return `${String(others)} ${others === 1 ? "other has" : "others have"} played today's daily so far.`;
  const share = Math.round((Math.min(e.below, others) / others) * 100);
  return `You did better than ${String(share)}% of the ${String(others)} others who played today.`;
}

/**
 * How everyone did at today's daily (ADR 0041): this play is counted (the browser's first of the
 * day is the one that counts) and shown against everyone's tiers, as counts, never names. Quiet
 * when the count can't be reached.
 */
export function EveryoneDaily({
  gameId,
  day,
  score,
  outcome,
}: {
  gameId: string;
  day: string;
  score: DailyScore;
  outcome: MatchOutcome;
}) {
  const tier = dailyTier(score, outcome);
  const [all, setAll] = useState<DailyEveryone | null>(null);
  useEffect(() => {
    let current = true;
    const value = Math.min(1_000_000, Math.max(0, Math.round(outcome.stats[score.stat] ?? 0)));
    api.countDaily(gameId, day, { tier, value }).then(
      (e) => {
        if (current) setAll(e);
      },
      () => {
        // Offline, or a play that ran past midnight: no comparison, nothing else changes.
      },
    );
    return () => {
      current = false;
    };
  }, [gameId, day, outcome]);
  if (!all || all.played < 1) return null;
  const most = Math.max(1, ...all.tiers);
  return (
    <section class="everyone-daily" aria-label="How everyone did today">
      <p>{everyoneText(all)}</p>
      <ol>
        {score.tiers.map((name, i) => (
          <li key={name} class={i === tier ? "mine" : undefined}>
            <span class="tier-name">
              {name}
              {i === tier && <span class="visually-hidden"> (you)</span>}
            </span>
            <span class="tier-bar" aria-hidden="true">
              <span style={{ width: `${String(((all.tiers[i] ?? 0) / most) * 100)}%` }} />
            </span>
            <span class="tier-count">{all.tiers[i] ?? 0}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
